/**
 * WebSocket connection to the game server (/ws).
 *
 * Responsibilities:
 *  - `hello` on every connect, `welcome` marks the connection as online;
 *  - automatic reconnects with exponential backoff + jitter, plus an immediate retry when
 *    the page becomes visible again or the browser regains network;
 *  - dead-connection detection: phones often keep a socket "open" that no longer delivers
 *    anything (backgrounded tab, network switch), so a watchdog drops connections that stay
 *    silent although we ping every few seconds — while the player races (setRacing) every
 *    second, and the connection gets 5 s instead of 10: the sooner a dead one is replaced, the
 *    sooner the others see the racer again and finished levels reach the server. A change of
 *    network (where the browser tells: Chrome on Android) checks the connection at once;
 *  - clock sync: a burst of pings after every connect, then one every few seconds;
 *  - positions (lossy messages) are skipped while the browser itself holds unsent data (a long
 *    stall; see LOSSY_BUFFER_BYTES);
 *  - an outbox for race events that keeps them across reconnects (see NetPort.flushOutbox).
 *    A socket that died silently still accepts data, so a reliable message only counts as
 *    delivered once a pong answers a ping sent after it (the server reads in order); until
 *    then it is kept and queued again if the connection breaks;
 *  - `leaving` when the page goes away (closed, reloaded): the server then keeps the seat
 *    only for a reload, instead of waiting as for a lost connection.
 *
 * The class has no DOM or framework dependencies; browser events come in via NetLifecycle.
 */
import { type ClientMessage, PROTOCOL_VERSION, type ServerMessage } from '@cube-rush/shared';
import { type BackoffOptions, backoffDelay, DEFAULT_BACKOFF } from './backoff';
import { ClockSync } from './clockSync';
import { type NetLifecycle, NO_LIFECYCLE } from './lifecycle';
import { Outbox } from './outbox';
import type {
  ConnectionStatus,
  HelloParams,
  NetPort,
  QueuedMessage,
  SendOptions,
  ServerMessageOf,
  ServerMessageType,
} from './types';

export interface NetTiming {
  /** A connection that has not received `welcome` after this long is retried. */
  connectTimeoutMs: number;
  /** Regular ping period (clock sync + keep-alive). */
  pingIntervalMs: number;
  /** The ping period while the player races. */
  racingPingIntervalMs: number;
  /** Pings sent right after connecting, for a quick clock estimate. */
  burstCount: number;
  burstSpacingMs: number;
  /** An online connection that receives nothing for this long is considered dead... */
  heartbeatTimeoutMs: number;
  /** ...while the player races, this long. */
  racingHeartbeatTimeoutMs: number;
  /** After the page wakes up, the connection must answer a ping within this time. */
  probeTimeoutMs: number;
  /** How often the watchdog checks the timeouts above. */
  watchdogMs: number;
}

export const DEFAULT_NET_TIMING: NetTiming = {
  connectTimeoutMs: 10_000,
  pingIntervalMs: 3_000,
  racingPingIntervalMs: 1_000,
  burstCount: 5,
  burstSpacingMs: 150,
  heartbeatTimeoutMs: 10_000,
  racingHeartbeatTimeoutMs: 5_000,
  probeTimeoutMs: 4_000,
  watchdogMs: 1_000,
};

/**
 * Lossy messages are skipped while more than this waits to be sent (WebSocket.bufferedAmount).
 * Only a safety net for a long stall: bufferedAmount grows only once the buffers below it (the
 * browser's network process, the system's socket: hundreds of KB, minutes of positions) are
 * full, so the positions of a stall of a few seconds still go out, all at once when the uplink
 * is back.
 */
export const LOSSY_BUFFER_BYTES = 4096;

export interface NetClientOptions {
  /** WebSocket URL, evaluated on every connect. */
  url: () => string;
  /** Builds `hello` on every connect; null means "not logged in" and stops the client. */
  hello: () => HelloParams | null;
  createSocket?: (url: string) => WebSocket;
  /** Monotonic clock in ms (performance.now in the browser). */
  now?: () => number;
  /** Wall clock, used only as the initial server-clock guess. */
  wallNow?: () => number;
  random?: () => number;
  lifecycle?: NetLifecycle;
  backoff?: Partial<BackoffOptions>;
  timing?: Partial<NetTiming>;
}

type Timeout = ReturnType<typeof setTimeout>;
type Interval = ReturnType<typeof setInterval>;
type Listener = (message: ServerMessage) => void;

/** A reliable message sent on the current connection and not confirmed by the server yet. */
interface InFlight {
  message: ClientMessage;
  tag: string;
  /** Id of the first ping sent after the message: its pong proves the message arrived. */
  confirmedBy: number;
}

export class NetClient implements NetPort {
  readonly clock: ClockSync;

  private statusValue: ConnectionStatus = 'idle';
  private started = false;
  private everOnline = false;
  private attempt = 0;
  /** The player races: pings come more often and silence is tolerated less (setRacing). */
  private racing = false;

  private socket: WebSocket | null = null;
  private detachSocket: (() => void) | null = null;
  private welcomed = false;
  private connectStartedAt = 0;
  private lastMessageAt = 0;
  private lastWatchdogAt = 0;
  private probeSentAt: number | null = null;
  private nextPingId = 1;

  private outboxHeld = true;
  private readonly outbox = new Outbox<ClientMessage>();
  private inFlight: InFlight[] = [];

  private retryTimer: Timeout | null = null;
  private watchdogTimer: Interval | null = null;
  private pingTimer: Interval | null = null;
  private burstTimers: Timeout[] = [];

  private readonly typedListeners = new Map<ServerMessageType, Set<Listener>>();
  private readonly anyListeners = new Set<Listener>();
  private readonly statusListeners = new Set<(status: ConnectionStatus) => void>();
  private readonly disposers: Array<() => void> = [];

  private readonly createSocket: (url: string) => WebSocket;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly backoff: BackoffOptions;
  private readonly timing: NetTiming;

  constructor(private readonly options: NetClientOptions) {
    this.createSocket = options.createSocket ?? ((url) => new WebSocket(url));
    this.now = options.now ?? (() => performance.now());
    this.random = options.random ?? Math.random;
    this.backoff = { ...DEFAULT_BACKOFF, ...options.backoff };
    this.timing = { ...DEFAULT_NET_TIMING, ...options.timing };
    this.clock = new ClockSync(this.now, options.wallNow);

    const lifecycle = options.lifecycle ?? NO_LIFECYCLE;
    this.disposers.push(
      lifecycle.onResume(() => this.handleResume()),
      lifecycle.onOnline(() => this.handleNetworkBack()),
      lifecycle.onNetworkChange(() => this.handleNetworkChange()),
      lifecycle.onLeave(() => this.handleLeave()),
    );
  }

  get status(): ConnectionStatus {
    return this.statusValue;
  }

  serverNow(): number {
    return this.clock.serverNow();
  }

  onStatus(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onMessage(listener: (message: ServerMessage) => void): () => void {
    this.anyListeners.add(listener);
    return () => this.anyListeners.delete(listener);
  }

  on<T extends ServerMessageType>(
    type: T,
    listener: (message: ServerMessageOf<T>) => void,
  ): () => void {
    let set = this.typedListeners.get(type);
    if (!set) {
      set = new Set();
      this.typedListeners.set(type, set);
    }
    // Safe: listeners in the set for `type` only ever receive messages of that type.
    const untyped = listener as Listener;
    set.add(untyped);
    return () => set.delete(untyped);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.everOnline = false;
    this.attempt = 0;
    this.connect();
  }

  stop(): void {
    this.started = false;
    this.clearRetry();
    this.outbox.clear();
    this.outboxHeld = true;
    this.closeSocket();
    this.setStatus('idle');
  }

  send(message: ClientMessage, options?: SendOptions): boolean {
    const socket = this.socket;
    if (!this.welcomed || !socket) return false;
    if (options?.lossy && socket.bufferedAmount > LOSSY_BUFFER_BYTES) return false;
    return this.transmit(socket, message);
  }

  sendReliable(message: ClientMessage, tag: string): void {
    const socket = this.socket;
    // Anything already queued goes first, so a new message waits behind it.
    if (socket && this.welcomed && !this.outboxHeld && this.outbox.size === 0) {
      this.deliver(socket, message, tag);
    } else {
      this.outbox.push(message, tag);
    }
  }

  setRacing(racing: boolean): void {
    if (this.racing === racing) return;
    this.racing = racing;
    // The next ping comes at the new pace (the watchdog reads the timeout on every check).
    if (this.pingTimer !== null) this.startPinging();
  }

  flushOutbox(keep: (queued: QueuedMessage) => boolean): void {
    const socket = this.socket;
    if (!this.welcomed || !socket) return;
    this.outboxHeld = false;
    const ready = this.outbox.drain(({ item, tag, maybeDelivered }) =>
      keep({ message: item, tag, maybeDelivered }),
    );
    for (const { item, tag } of ready) this.deliver(socket, item, tag);
  }

  dispose(): void {
    this.stop();
    for (const dispose of this.disposers.splice(0)) dispose();
    this.typedListeners.clear();
    this.anyListeners.clear();
    this.statusListeners.clear();
  }

  // ---- connection lifecycle ----

  private connect(): void {
    this.clearRetry();
    let socket: WebSocket;
    try {
      socket = this.createSocket(this.options.url());
    } catch {
      this.scheduleRetry();
      return;
    }
    const onOpen = () => this.handleOpen(socket);
    const onMessage = (event: MessageEvent) => this.handleData(event.data);
    const onClose = () => {
      if (this.socket === socket) this.handleSocketGone();
    };
    socket.addEventListener('open', onOpen);
    socket.addEventListener('message', onMessage);
    socket.addEventListener('close', onClose);
    this.detachSocket = () => {
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('close', onClose);
    };
    this.socket = socket;
    this.welcomed = false;
    this.probeSentAt = null;
    this.connectStartedAt = this.now();
    this.lastMessageAt = this.connectStartedAt;
    this.lastWatchdogAt = this.connectStartedAt;
    this.watchdogTimer = setInterval(() => this.watchdog(), this.timing.watchdogMs);
    this.setStatus(this.everOnline ? 'reconnecting' : 'connecting');
  }

  private handleOpen(socket: WebSocket): void {
    const hello = this.options.hello();
    if (!hello) {
      this.stop();
      return;
    }
    this.transmit(socket, { t: 'hello', v: PROTOCOL_VERSION, ...hello });
  }

  private handleData(data: unknown): void {
    if (typeof data !== 'string') return;
    let message: unknown;
    try {
      message = JSON.parse(data);
    } catch {
      return;
    }
    if (!isServerMessage(message)) return;
    this.lastMessageAt = this.now();
    this.probeSentAt = null;
    if (message.t === 'welcome') {
      this.handleWelcome(message);
    } else if (message.t === 'pong') {
      this.clock.addSample(message.ct, message.st, this.lastMessageAt);
      this.confirmDelivery(message.id);
    }
    this.emit(message);
  }

  private handleWelcome(message: ServerMessageOf<'welcome'>): void {
    this.welcomed = true;
    this.everOnline = true;
    this.attempt = 0;
    this.outboxHeld = true;
    this.clock.reset();
    this.clock.seed(message.serverTime);
    this.sendPingBurst();
    this.startPinging();
    this.setStatus('online');
  }

  /** (Re)starts the regular pings at the current pace: one ping timer per connection. */
  private startPinging(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    const { pingIntervalMs, racingPingIntervalMs } = this.timing;
    this.pingTimer = setInterval(
      () => this.ping(),
      this.racing ? racingPingIntervalMs : pingIntervalMs,
    );
  }

  /** The socket closed or was dropped: clean up and retry unless stopped. */
  private handleSocketGone(): void {
    this.detachSocket?.();
    this.detachSocket = null;
    this.socket = null;
    this.welcomed = false;
    this.stopTimers();
    // Whatever the server has not confirmed may have been lost with the connection.
    this.outbox.requeue(this.inFlight.map(({ message, tag }) => ({ item: message, tag })));
    this.inFlight = [];
    if (this.started) this.scheduleRetry();
    else this.setStatus('idle');
  }

  /** Closes the current socket without waiting for its close event (dead sockets may never send one). */
  private dropSocket(): void {
    const socket = this.socket;
    if (!socket) return;
    this.detachSocket?.();
    this.detachSocket = null;
    safeClose(socket);
    this.handleSocketGone();
  }

  private closeSocket(): void {
    const socket = this.socket;
    this.detachSocket?.();
    this.detachSocket = null;
    this.socket = null;
    this.welcomed = false;
    this.inFlight = [];
    this.stopTimers();
    if (socket) safeClose(socket, 1000);
  }

  private scheduleRetry(): void {
    this.clearRetry();
    const delay = backoffDelay(this.attempt, this.random, this.backoff);
    this.attempt += 1;
    this.setStatus(this.everOnline ? 'reconnecting' : 'connecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private stopTimers(): void {
    if (this.watchdogTimer !== null) clearInterval(this.watchdogTimer);
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    for (const timer of this.burstTimers) clearTimeout(timer);
    this.watchdogTimer = null;
    this.pingTimer = null;
    this.burstTimers = [];
    this.probeSentAt = null;
  }

  private watchdog(): void {
    const now = this.now();
    const sinceLastTick = now - this.lastWatchdogAt;
    this.lastWatchdogAt = now;
    if (!this.welcomed) {
      if (now - this.connectStartedAt > this.timing.connectTimeoutMs) this.dropSocket();
      return;
    }
    // A long gap between ticks means timers were frozen (page in the background): the
    // silence is ours, not the server's, so ask before declaring the connection dead.
    if (sinceLastTick > this.timing.watchdogMs * 3 && this.probeSentAt === null) {
      this.probe(true);
      return;
    }
    if (this.probeSentAt !== null) {
      if (now - this.probeSentAt > this.timing.probeTimeoutMs) this.dropSocket();
      return;
    }
    const { heartbeatTimeoutMs, racingHeartbeatTimeoutMs } = this.timing;
    const timeout = this.racing ? racingHeartbeatTimeoutMs : heartbeatTimeoutMs;
    if (now - this.lastMessageAt > timeout) this.dropSocket();
  }

  // ---- pings & clock ----

  private ping(): void {
    this.send({ t: 'ping', id: this.nextPingId++, ct: this.now() });
  }

  private sendPingBurst(): void {
    for (const timer of this.burstTimers) clearTimeout(timer);
    this.burstTimers = [];
    this.ping();
    for (let i = 1; i < this.timing.burstCount; i++) {
      this.burstTimers.push(setTimeout(() => this.ping(), i * this.timing.burstSpacingMs));
    }
  }

  /**
   * Verifies that the connection still answers (a burst of pings, which also brings fresh clock
   * samples); `resync` first forgets the old samples (after the device slept).
   */
  private probe(resync: boolean): void {
    if (resync) this.clock.reset();
    this.probeSentAt = this.now();
    this.sendPingBurst();
  }

  private handleResume(): void {
    if (!this.started) return;
    if (!this.socket) this.connect();
    else if (this.welcomed) this.probe(true);
  }

  private handleNetworkBack(): void {
    if (this.started && !this.socket) this.connect();
  }

  /**
   * Another network (Wi-Fi to mobile data and back): the connection may well have died with the
   * old one. It must answer at once (a probe); a connection being made goes on. The clock keeps
   * its samples: Chrome reports a new estimate of the network's speed the same way, often, and
   * a connection that did die gets a fresh clock with its replacement anyway.
   */
  private handleNetworkChange(): void {
    if (!this.started) return;
    if (!this.socket) this.connect();
    else if (this.welcomed && this.probeSentAt === null) this.probe(false);
  }

  /**
   * The page is going away. Said while online only: the message goes out ahead of the close
   * the browser starts right after. Offline, the seat already waits as for a lost connection.
   */
  private handleLeave(): void {
    this.send({ t: 'leaving' });
  }

  // ---- plumbing ----

  private transmit(socket: WebSocket, message: ClientMessage): boolean {
    // A closing socket accepts send() but silently drops the data.
    if (socket.readyState !== socket.OPEN) return false;
    try {
      socket.send(JSON.stringify(message));
      return true;
    } catch {
      return false;
    }
  }

  /** Sends a reliable message and keeps it until the server confirms it (see confirmDelivery). */
  private deliver(socket: WebSocket, message: ClientMessage, tag: string): void {
    if (this.transmit(socket, message)) {
      this.inFlight.push({ message, tag, confirmedBy: this.nextPingId });
    } else {
      this.outbox.push(message, tag);
    }
  }

  /** The server answers pings in order: a pong proves it has read everything sent before. */
  private confirmDelivery(pingId: number): void {
    if (this.inFlight.length === 0) return;
    this.inFlight = this.inFlight.filter((sent) => sent.confirmedBy > pingId);
  }

  private emit(message: ServerMessage): void {
    const typed = this.typedListeners.get(message.t);
    for (const listener of [...(typed ?? []), ...this.anyListeners]) {
      try {
        listener(message);
      } catch (error) {
        // One broken listener must not stop the others or kill the connection.
        console.error(`NetClient: listener for "${message.t}" failed`, error);
      }
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.statusValue === status) return;
    this.statusValue = status;
    for (const listener of [...this.statusListeners]) listener(status);
  }
}

function isServerMessage(value: unknown): value is ServerMessage {
  return typeof value === 'object' && value !== null && typeof Reflect.get(value, 't') === 'string';
}

function safeClose(socket: WebSocket, code?: number): void {
  try {
    socket.close(code);
  } catch {
    // Already closing or closed.
  }
}
